import { Shield, Accessibility, Eye, User, Linkedin } from 'lucide-react'
import Section, { SectionHeader } from '../components/Section'
import Button from '../components/Button'
import Card, { FeatureCard } from '../components/Card'

const teamMembers = [
    { name: 'Alex Rivera', role: 'Co-Founder & CEO', bio: 'Former founder. Obsessed with voice AI and workflow automation.' },
    { name: 'Jordan Park', role: 'Co-Founder & CTO', bio: 'Built ML systems at Google. Expert in low-latency voice architecture.' },
    { name: 'Sam Williams', role: 'Head of Product', bio: 'Previously at Notion. Focused on neurodiversity-friendly design.' },
    { name: 'Casey Morgan', role: 'Head of Engineering', bio: '10+ years building scalable systems. Former Amazon.' },
]

export default function About() {
    return (
        <>
            {/* Hero Section */}
            <Section className="pt-12 lg:pt-20">
                <div className="text-center max-w-4xl mx-auto">
                    <h1 className="text-4xl lg:text-5xl font-bold text-primary-color leading-tight mb-6">
                        Building the Future of Work
                    </h1>
                    <p className="text-lg lg:text-xl font-normal text-secondary-color leading-relaxed mb-8">
                        We're pioneering the AI Chief of Staff category—software that doesn't just organize, but actively works for you.
                    </p>
                    <div className="flex flex-wrap justify-center gap-8 mt-8">
                        <div className="text-center">
                            <p className="text-2xl font-bold text-primary-color">2025</p>
                            <p className="text-sm text-secondary-color">Founded</p>
                        </div>
                        <div className="text-center">
                            <p className="text-2xl font-bold text-primary-color">10,000+</p>
                            <p className="text-sm text-secondary-color">Users</p>
                        </div>
                        <div className="text-center">
                            <p className="text-2xl font-bold text-primary-color">100%</p>
                            <p className="text-sm text-secondary-color">Productivity Obsessed</p>
                        </div>
                    </div>
                </div>
            </Section>

            {/* Mission & Vision */}
            <Section glass>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                    <Card variant="elevated" className="p-8">
                        <h2 className="text-2xl font-semibold text-primary-color mb-4">Our Mission</h2>
                        <p className="text-secondary-color leading-relaxed">
                            To eliminate the administrative burden that prevents people from doing their best work.
                            We believe productivity tools should reduce cognitive load, not add to it.
                        </p>
                    </Card>
                    <Card variant="elevated" className="p-8">
                        <h2 className="text-2xl font-semibold text-primary-color mb-4">Our Vision</h2>
                        <p className="text-secondary-color leading-relaxed">
                            A world where everyone has access to an AI Chief of Staff—intelligent software that captures intent,
                            organizes context, and executes tasks autonomously, making peak productivity effortless.
                        </p>
                    </Card>
                </div>
            </Section>

            {/* The Agentic Era */}
            <Section>
                <SectionHeader
                    title="Why Now? The Agentic Shift"
                    subtitle="For the past decade, productivity software has been passive. We're entering a new era."
                />

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
                    <Card variant="default" className="p-6 text-center">
                        <p className="text-sm font-semibold text-tertiary-color mb-2">2000-2010</p>
                        <h3 className="text-lg font-semibold text-primary-color mb-2">Desktop Era</h3>
                        <p className="text-sm text-secondary-color">Local files, manual management</p>
                        <p className="text-xs text-tertiary-color mt-2">Microsoft Office</p>
                    </Card>
                    <Card variant="default" className="p-6 text-center">
                        <p className="text-sm font-semibold text-tertiary-color mb-2">2010-2024</p>
                        <h3 className="text-lg font-semibold text-primary-color mb-2">Cloud/Mobile Era</h3>
                        <p className="text-sm text-secondary-color">Passive containers, manual input</p>
                        <p className="text-xs text-tertiary-color mt-2">Notion, Trello, Asana</p>
                    </Card>
                    <Card variant="elevated" className="p-6 text-center border-2 border-primary-base/30">
                        <p className="text-sm font-semibold text-primary-base mb-2">2024+</p>
                        <h3 className="text-lg font-semibold text-primary-color mb-2">Agentic Era</h3>
                        <p className="text-sm text-secondary-color">Active agents, autonomous execution</p>
                        <p className="text-xs text-primary-base mt-2 font-semibold">Way2Will</p>
                    </Card>
                </div>

                <div className="glass p-8 rounded-2xl">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
                        <div>
                            <p className="text-2xl font-bold text-primary-color">$36B</p>
                            <p className="text-sm text-secondary-color">Productivity market by 2033</p>
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-primary-color">$50M ARR</p>
                            <p className="text-sm text-secondary-color">Motion's validation of AI scheduling</p>
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-primary-color">$34/mo</p>
                            <p className="text-sm text-secondary-color">Users paying for "AI Employees"</p>
                        </div>
                    </div>
                </div>
            </Section>

            {/* Founding Story */}
            <Section glass>
                <SectionHeader title="How We Got Here" />
                <div className="max-w-3xl mx-auto">
                    <div className="prose prose-lg">
                        <p className="text-secondary-color mb-6 leading-relaxed">
                            Way2Will was born from a simple frustration: every productivity tool on the market demanded more work
                            than it saved. Our founders—serial entrepreneurs and neurodivergent creators—spent years fighting
                            with rigid algorithms and lifeless to-do lists.
                        </p>
                        <p className="text-secondary-color mb-6 leading-relaxed">
                            The release of multimodal AI models like Gemini 2.0 and GPT-4o finally made true voice-first
                            intelligence possible. We saw an opportunity to build what we'd always wanted: an AI that listens,
                            remembers, and acts like a real Chief of Staff.
                        </p>
                        <p className="text-secondary-color leading-relaxed">
                            Today, Way2Will serves thousands of founders, creators, and high-performers who've replaced their
                            administrative overhead with our AI. We're just getting started.
                        </p>
                    </div>
                </div>
            </Section>

            {/* Values */}
            <Section>
                <SectionHeader title="What We Stand For" />
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                    <FeatureCard
                        icon={<Shield className="w-full h-full" />}
                        title="Privacy First"
                        description="GDPR and BIPA compliant. We'll never sell your data or use it to train third-party models."
                    />
                    <FeatureCard
                        icon={<Accessibility className="w-full h-full" />}
                        title="Designed for Neurodiversity"
                        description="We build for brains that work differently. Our AI acts as an external executive function."
                    />
                    <FeatureCard
                        icon={<Eye className="w-full h-full" />}
                        title="Radical Transparency"
                        description="No dark patterns. No hidden fees. We tell you exactly how the AI works."
                    />
                    <FeatureCard
                        icon={<User className="w-full h-full" />}
                        title="Human Autonomy"
                        description="AI should augment human agency, not replace it. Way2Will negotiates with you, never dictates."
                    />
                </div>
            </Section>

            {/* Team */}
            <Section glass>
                <SectionHeader
                    title="Meet the Team"
                    subtitle="We're a distributed team of engineers, designers, and productivity nerds united by a mission to make work effortless."
                />
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                    {teamMembers.map((member) => (
                        <Card key={member.name} variant="default" className="p-6 text-center">
                            <div className="w-20 h-20 mx-auto mb-4 rounded-full gradient-primary flex items-center justify-center">
                                <span className="text-2xl font-bold text-white/85">{member.name.charAt(0)}</span>
                            </div>
                            <h3 className="text-lg font-semibold text-primary-color">{member.name}</h3>
                            <p className="text-sm text-primary-base mb-2">{member.role}</p>
                            <p className="text-sm text-secondary-color mb-4">{member.bio}</p>
                            <a href="#" className="inline-flex items-center gap-1 text-sm text-primary-base hover:text-primary-darker">
                                <Linkedin className="w-4 h-4" />
                            </a>
                        </Card>
                    ))}
                </div>
                <div className="text-center mt-8">
                    <Button variant="ghost" to="#">We're Hiring! Join Us →</Button>
                </div>
            </Section>

            {/* Press */}
            <Section>
                <SectionHeader title="Featured In" />
                <div className="flex flex-wrap justify-center items-center gap-12 opacity-60 mb-12">
                    <span className="text-2xl font-bold text-tertiary-color">TechCrunch</span>
                    <span className="text-2xl font-bold text-tertiary-color">Product Hunt</span>
                    <span className="text-2xl font-bold text-tertiary-color">Hacker News</span>
                    <span className="text-2xl font-bold text-tertiary-color">Fast Company</span>
                    <span className="text-2xl font-bold text-tertiary-color">The Verge</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
                    <Card variant="default" className="p-6">
                        <p className="text-secondary-color italic mb-4">"Way2Will is rethinking productivity from the ground up."</p>
                        <p className="text-sm font-semibold text-primary-color">— TechCrunch</p>
                    </Card>
                    <Card variant="default" className="p-6">
                        <p className="text-secondary-color italic mb-4">"Finally, an AI assistant that feels human."</p>
                        <p className="text-sm font-semibold text-primary-color">— The Verge</p>
                    </Card>
                    <Card variant="default" className="p-6">
                        <p className="text-secondary-color italic mb-4">"The future of personal productivity."</p>
                        <p className="text-sm font-semibold text-primary-color">— Product Hunt</p>
                    </Card>
                </div>
            </Section>

            {/* CTA */}
            <Section glass>
                <div className="max-w-4xl mx-auto text-center">
                    <div className="glass-prominent p-12 lg:p-16 rounded-2xl">
                        <h2 className="text-3xl lg:text-4xl font-semibold text-primary-color mb-6">
                            Join the Agentic Revolution
                        </h2>
                        <p className="text-lg text-secondary-color mb-8">
                            Be part of the shift from passive tools to active agents.
                        </p>
                        <div className="flex flex-wrap justify-center gap-4">
                            <Button to="/signup" size="lg">Start Your Free Trial</Button>
                            <Button variant="secondary" size="lg">Explore Careers</Button>
                        </div>
                    </div>
                </div>
            </Section>
        </>
    )
}
